import * as React from "react";
import { useAsync } from "react-use";
import { useQueryClient } from "@tanstack/react-query";
import { useAccountStore, useAppStore } from "@/stores";
import { getAuth } from "@/services/career";
import { privateRoutes, publicRoutes } from "@/routes";
import { redirectToLoginPage } from "@/utils/others";
import { ParsedUrlQuery } from "querystring";
import { isInPrivateRoute } from "@/utils/urls/routeHelper";
import { isHeadhunterEmailCampaign } from "@/utils/urls/isHeadhunterEmailCampaign";
import { GET_AUTH_STATUS, meQuery } from "@/react-query/queries";
import { getLanguageBasedOnDomain } from "@/utils/urls";
import { writeNavbarAccountStateCookie } from "@/utils/helpers/navbarAccountState";
function useAuthenticate(skip: boolean, pathname: string, query?: ParsedUrlQuery) {
  const {
    setAccountDetails,
    setIsAccountResolved
  } = useAccountStore();
  const [shouldAppHydrate, setShouldAppHydrate] = React.useState(false);
  const {
    updateGeneralAppState
  } = useAppStore();
  const queryClient = useQueryClient();
  const me = typeof query?.me === "string" ? query.me : undefined;
  // for checking onboarding flow
  const flow = typeof query?.flow === "string" ? query.flow : undefined;
  const isOnboardingCareerFlow = flow === "career";
  const isForbiddenForLoggedInUser = [publicRoutes.onboarding, publicRoutes.login, publicRoutes.registration].some(route => route === pathname);
  const isPaymentRoute = pathname === privateRoutes.payment;
  const isPublicRoute = Object.values(publicRoutes).some(route_0 => pathname.startsWith(route_0));
  const syncLoggedOutLanguage = React.useCallback(() => {
    const domainLanguage = getLanguageBasedOnDomain(window.location.href);
    if (domainLanguage) {
      updateGeneralAppState({
        language: domainLanguage
      });
    }
  }, [updateGeneralAppState]);

  // check authentication
  useAsync(async () => {
    // wait for:
    // - router to be ready for use
    // - growthbook to load features
    if (skip) return;
    setIsAccountResolved(false);
    try {
      // skip request when `me` is false [redundant request]
      if (me !== "false") {
        if (isPublicRoute && !isOnboardingCareerFlow) {
          await queryClient.fetchQuery({
            queryKey: [GET_AUTH_STATUS],
            queryFn: () => getAuth({
              silentSentry: [404],
              skipRedirection: true
            })
          });
        }
        // fetchQuery dedupes concurrent/repeated calls across every mounted
        // `useAuthenticate` (page provider + payment modal) instead of firing
        // a `/career/me` request per mount and per route change.
        const accountDetails = await queryClient.fetchQuery(meQuery);
        setAccountDetails(accountDetails);
        writeNavbarAccountStateCookie({
          isLoggedIn: Boolean(accountDetails?.id),
          isPremium: accountDetails?.premium === true
        });

        // premium users can't access payment page
        if (isPaymentRoute && accountDetails?.premium) {
          return window.location.assign("/");
        }

        // set app language, update happens in separated effect
        if (accountDetails?.language?.code) {
          updateGeneralAppState({
            language: accountDetails.language?.code
          });
        }

        // user exits "Candidate UI" and visits PJPP home page,
        // so it doesn't need to hydrate application
        if (isForbiddenForLoggedInUser && !isOnboardingCareerFlow) {
          return window.location.assign("/");
        }
      }
      if (me === "false") {
        setIsAccountResolved(true);
        syncLoggedOutLanguage();
        writeNavbarAccountStateCookie({
          isLoggedIn: false,
          isPremium: false
        });
      }
      setShouldAppHydrate(true);
    } catch (error) {
      const isPrivatePath = isInPrivateRoute(pathname);
      if (isPrivatePath || isOnboardingCareerFlow || isHeadhunterEmailCampaign(pathname)) {
        return redirectToLoginPage();
      }
      syncLoggedOutLanguage();
      setIsAccountResolved(true);
      writeNavbarAccountStateCookie({
        isLoggedIn: false,
        isPremium: false
      });
      setShouldAppHydrate(true);
    }
  }, [isForbiddenForLoggedInUser, isPublicRoute, skip, me, setAccountDetails, setIsAccountResolved, syncLoggedOutLanguage, queryClient]);
  return shouldAppHydrate;
}
export default useAuthenticate;